import { useEffect, useRef, useState } from 'react';
import { Modal, ModalHeader, ModalBody, ModalFooter, Form, FormGroup, Label, Input, InputGroup, InputGroupText, FormFeedback, Button, Spinner, Row, Col, Progress } from 'reactstrap';
import { BiUpload } from 'react-icons/bi';
import { toast } from 'react-toastify';
import useCreateInvoice from '../../hooks/useCreateInvoice';
import useCreateInvoiceFromAttachment from '../../hooks/useCreateInvoiceFromAttachment';
import useDiscardScanJob from '../../hooks/useDiscardScanJob';
import useCategories, { CATEGORY_TARGET } from '../../hooks/useCategories';
import { queueInvoiceScan, checkInvoiceDuplicates } from '../../api/invoicesApi';
import { currencySymbol, CURRENCIES } from '../../constants/currencies';
import DuplicateWarning from '../common/DuplicateWarning';

// Revenue-side twin of createExpenseModal. Mirrors the expense modal's state
// machine + upload loop — see that file for the detailed commentary that
// applies here verbatim (server-side blob ownership, serial multipart, Fill
// manually recovery via useCreateInvoiceFromAttachment, etc.).

const MAX_BYTES = 10 * 1024 * 1024;
const MAX_FILES = 20;
const ACCEPT_TYPES = 'image/*,application/pdf';

function validateInvoice({ amount, vatAmount, invoiceDate }) {
    const errors = {};
    if (!invoiceDate)                    errors.invoiceDate = 'Invoice date is required.';
    if (amount === '' || amount == null) errors.amount      = 'Amount is required.';
    else if (Number.isNaN(Number(amount)) || Number(amount) < 0)
                                         errors.amount      = 'Amount must be zero or positive.';
    if (vatAmount !== '' && vatAmount != null) {
        if (Number.isNaN(Number(vatAmount)) || Number(vatAmount) < 0)
            errors.vatAmount = 'VAT amount must be zero or positive.';
    }
    return { errors, hasErrors: Object.keys(errors).length > 0 };
}

const todayLocal = () => { const d = new Date(); return `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-${String(d.getDate()).padStart(2,'0')}`; };

export default function CreateInvoiceModal({ isOpen, onClose, jobId, currency = 'GBP', pendingScanJob = null }) {
    const createInvoice = useCreateInvoice();
    const createInvoiceFromAttachment = useCreateInvoiceFromAttachment();
    const discardScanJob = useDiscardScanJob();
    const categories = useCategories(CATEGORY_TARGET.Invoice);

    const [mode, setMode] = useState(pendingScanJob ? 'manual' : 'intro');
    const [uploadCount, setUploadCount] = useState(0);
    const [uploadTotal, setUploadTotal] = useState(0);
    const [dragActive, setDragActive] = useState(false);

    const [invoiceDate, setInvoiceDate] = useState(todayLocal());
    const [customer, setCustomer]       = useState('');
    const [description, setDescription] = useState('');
    const [category, setCategory]       = useState('');
    const [amount, setAmount]           = useState('');
    const [vatAmount, setVatAmount]     = useState('');
    // Per-invoice currency, prefilled from the job; editable so a job can hold
    // mixed-currency rows.
    const [currencyValue, setCurrencyValue] = useState(currency);
    const [attempted, setAttempted]     = useState(false);
    const [dupMatches, setDupMatches]   = useState([]);
    const [dupChecking, setDupChecking] = useState(false);

    const fileInputRef = useRef(null);

    const { errors, hasErrors } = validateInvoice({ amount, vatAmount, invoiceDate });

    useEffect(() => {
        if (!isOpen) return;
        setCurrencyValue(currency);   // re-sync to the job currency on (re)open
        if (pendingScanJob) {
            setMode('manual');
            setCustomer('');
        } else {
            setMode('intro');
        }
    }, [isOpen, pendingScanJob, currency]);

    const resetFormFields = () => {
        setInvoiceDate(todayLocal());
        setCustomer(''); setDescription(''); setCategory('');
        setAmount(''); setVatAmount('');
        setCurrencyValue(currency);
        setAttempted(false);
        setDupMatches([]);
        setDupChecking(false);
    };
    const resetAll = () => {
        setMode(pendingScanJob ? 'manual' : 'intro');
        setUploadCount(0); setUploadTotal(0);
        setDragActive(false);
        resetFormFields();
    };

    const handleClose = () => {
        resetAll();
        onClose();
    };

    // ---- Intro: drag-and-drop / file picker → serial QueueInvoiceScan ----

    const handleFiles = async (fileList) => {
        const files = Array.from(fileList || []);
        if (files.length === 0) return;

        if (files.length > MAX_FILES) {
            toast.error(`Too many files (max ${MAX_FILES}).`);
            return;
        }
        for (const f of files) {
            if (f.size > MAX_BYTES) {
                toast.error(`"${f.name}" is too large (max 10 MB).`);
                return;
            }
        }

        setMode('uploading');
        setUploadTotal(files.length);
        setUploadCount(0);

        let succeeded = 0;
        for (let i = 0; i < files.length; i++) {
            try {
                await queueInvoiceScan(jobId, files[i]);
                succeeded += 1;
                setUploadCount(succeeded);
            } catch (err) {
                toast.error(err?.message ?? `Failed to upload "${files[i].name}"`);
                // A 403 means the scan limit has been reached — no point
                // attempting the remaining files since they'll all fail too.
                if (err?.status === 403) break;
            }
        }

        if (succeeded > 0) {
            // Silent — rows appearing in the table is confirmation enough.
        }
        resetAll();
        onClose();
    };

    const handlePick = (e) => {
        const files = Array.from(e.target.files || []);
        e.target.value = '';
        handleFiles(files);
    };
    const onDragOver  = (e) => { e.preventDefault(); setDragActive(true); };
    const onDragLeave = (e) => { e.preventDefault(); setDragActive(false); };
    const onDrop      = (e) => {
        e.preventDefault();
        setDragActive(false);
        handleFiles(e.dataTransfer?.files);
    };

    // ---- Manual submit: plain form OR fill-manually recovery commit ----

    const busy = createInvoice.isPending || createInvoiceFromAttachment.isPending;

    // First submit runs the duplicate check; matches show the warning + "Save
    // anyway". A failed check never blocks the save (advisory warning).
    const submitManual = async (e) => {
        e.preventDefault();
        if (busy || dupChecking) return;
        if (hasErrors) { setAttempted(true); return; }

        setDupChecking(true);
        let matches = [];
        try {
            matches = await checkInvoiceDuplicates({
                amount: Number(amount),
                date: invoiceDate,
                customer: customer.trim() || undefined,
            });
        } catch { /* ignore — warning is advisory */ }
        setDupChecking(false);
        if (matches.length > 0) { setDupMatches(matches); return; }

        await doSave();
    };

    const doSave = async () => {
        if (busy) return;
        if (pendingScanJob) {
            await createInvoiceFromAttachment.mutateAsync({
                jobId,
                invoiceDate,
                customer:    customer.trim()    || null,
                description: description.trim() || null,
                categoryId:  category === '' ? null : Number(category),
                amount:      Number(amount),
                vatAmount:   vatAmount === '' ? null : Number(vatAmount),
                currency:    currencyValue,
                pendingBlobPath:  pendingScanJob.pendingBlobPath,
                originalFileName: pendingScanJob.originalFileName,
                contentType:      pendingScanJob.contentType,
                fileSizeByBytes:  pendingScanJob.fileSizeByBytes,
            });
            await discardScanJob.mutateAsync(pendingScanJob.id).catch(() => { /* row gone is fine */ });
        } else {
            await createInvoice.mutateAsync({
                jobId,
                invoiceDate,
                customer:    customer.trim()    || null,
                description: description.trim() || null,
                categoryId:  category === '' ? null : Number(category),
                amount:      Number(amount),
                vatAmount:   vatAmount === '' ? null : Number(vatAmount),
                currency:    currencyValue,
            });
        }
        resetAll();
        onClose();
    };

    // ---- Render helpers ----

    const formFields = (
        <>
            <DuplicateWarning matches={dupMatches} currency={currencyValue} />
            <Row>
                <Col md={6}>
                    <FormGroup>
                        <Label>Invoice Date *</Label>
                        <Input
                            type="date"
                            value={invoiceDate}
                            onChange={(e) => setInvoiceDate(e.target.value)}
                            invalid={attempted && !!errors.invoiceDate}
                        />
                        <FormFeedback>{errors.invoiceDate}</FormFeedback>
                    </FormGroup>
                </Col>
                <Col md={6}>
                    <FormGroup>
                        <Label>Category</Label>
                        <Input
                            type="select"
                            value={category}
                            onChange={(e) => setCategory(e.target.value)}
                            disabled={categories.isLoading}
                        >
                            <option value="">— Select category —</option>
                            {(categories.data ?? []).map((c) => (
                                <option key={c.id} value={c.id}>{c.name}</option>
                            ))}
                        </Input>
                    </FormGroup>
                </Col>
            </Row>
            <FormGroup>
                <Label>Customer</Label>
                <Input value={customer} onChange={(e) => setCustomer(e.target.value)} />
            </FormGroup>
            <FormGroup>
                <Label>Description</Label>
                <Input
                    type="textarea"
                    rows={2}
                    value={description}
                    onChange={(e) => setDescription(e.target.value)}
                />
            </FormGroup>
            <FormGroup>
                <Label>Currency</Label>
                <Input
                    type="select"
                    value={currencyValue}
                    onChange={(e) => setCurrencyValue(e.target.value)}
                    style={{ maxWidth: 240 }}
                >
                    {CURRENCIES.map((c) => (
                        <option key={c.code} value={c.code}>{c.symbol} {c.code} — {c.name}</option>
                    ))}
                </Input>
            </FormGroup>
            <Row>
                <Col md={6}>
                    <FormGroup>
                        <Label>Amount *</Label>
                        <InputGroup>
                            <InputGroupText>{currencySymbol(currencyValue)}</InputGroupText>
                            <Input
                                type="number"
                                step="0.01"
                                min="0"
                                value={amount}
                                onChange={(e) => setAmount(e.target.value)}
                                invalid={attempted && !!errors.amount}
                            />
                            <FormFeedback>{errors.amount}</FormFeedback>
                        </InputGroup>
                    </FormGroup>
                </Col>
                <Col md={6}>
                    <FormGroup>
                        <Label>VAT Amount</Label>
                        <InputGroup>
                            <InputGroupText>{currencySymbol(currencyValue)}</InputGroupText>
                            <Input
                                type="number"
                                step="0.01"
                                min="0"
                                value={vatAmount}
                                onChange={(e) => setVatAmount(e.target.value)}
                                invalid={attempted && !!errors.vatAmount}
                            />
                            <FormFeedback>{errors.vatAmount}</FormFeedback>
                        </InputGroup>
                    </FormGroup>
                </Col>
            </Row>
        </>
    );

    const headerText = (() => {
        if (mode === 'uploading') return 'Uploading invoices…';
        if (mode === 'manual')    return pendingScanJob
            ? `Fill manually — ${pendingScanJob.originalFileName ?? 'invoice'}`
            : 'New Invoice';
        return 'New Invoice';
    })();

    const uploadPct = uploadTotal > 0 ? Math.round((uploadCount / uploadTotal) * 100) : 0;

    return (
        <Modal isOpen={isOpen} toggle={handleClose} centered backdrop="static" size="lg">
            <ModalHeader toggle={handleClose}>{headerText}</ModalHeader>

            {mode === 'intro' && (
                <>
                    <ModalBody>
                        <div
                            onDragOver={onDragOver}
                            onDragLeave={onDragLeave}
                            onDrop={onDrop}
                            onClick={() => fileInputRef.current?.click()}
                            role="button"
                            tabIndex={0}
                            onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') fileInputRef.current?.click(); }}
                            className={`border border-2 rounded d-flex flex-column align-items-center justify-content-center text-center p-5 ${dragActive ? 'border-primary bg-light' : 'border-secondary-subtle'}`}
                            style={{ cursor: 'pointer', minHeight: 220 }}
                        >
                            <BiUpload size={48} className="mb-2 text-secondary" />
                            <div className="fw-semibold">Drop invoices here</div>
                            <div className="text-muted small">
                                or click to browse — images or PDF, up to 10 MB each
                            </div>
                            <div className="text-muted small mt-2">
                                Invoices process in the background; rows appear in the table as each finishes.
                            </div>
                        </div>
                        <input
                            ref={fileInputRef}
                            type="file"
                            hidden
                            multiple
                            accept={ACCEPT_TYPES}
                            onChange={handlePick}
                        />
                        <div className="text-center mt-3">
                            <Button color="link" size="sm" onClick={() => { resetFormFields(); setMode('manual'); }}>
                                Enter manually instead
                            </Button>
                        </div>
                    </ModalBody>
                    <ModalFooter>
                        <Button color="secondary" outline onClick={handleClose}>Cancel</Button>
                    </ModalFooter>
                </>
            )}

            {mode === 'uploading' && (
                <>
                    <ModalBody>
                        <div className="d-flex flex-column align-items-center justify-content-center py-4">
                            <div className="fw-semibold mb-2">
                                Uploading invoice {Math.min(uploadCount + 1, uploadTotal)} of {uploadTotal}…
                            </div>
                            <Progress value={uploadPct} className="w-100" style={{ height: 10 }} />
                            <div className="text-muted small mt-2">
                                The modal closes when uploads finish; OCR runs in the background.
                            </div>
                        </div>
                    </ModalBody>
                </>
            )}

            {mode === 'manual' && (
                <Form onSubmit={submitManual} noValidate>
                    <ModalBody>{formFields}</ModalBody>
                    <ModalFooter>
                        <Button color="secondary" outline onClick={handleClose}
                                disabled={busy || dupChecking}>Cancel</Button>
                        {dupMatches.length > 0 ? (
                            <Button color="warning" type="button" onClick={doSave} disabled={busy}>
                                {busy && <Spinner size="sm" className="me-1" />}
                                Save anyway
                            </Button>
                        ) : (
                            <Button color="primary" type="submit" disabled={busy || dupChecking}>
                                {(busy || dupChecking) && <Spinner size="sm" className="me-1" />}
                                {pendingScanJob ? 'Save Invoice' : 'Create'}
                            </Button>
                        )}
                    </ModalFooter>
                </Form>
            )}
        </Modal>
    );
}
